import React, { useState } from 'react'
import { Box, Skeleton, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { Bar } from 'react-chartjs-2'
import { allZero, axes, baseOptions, htmlTooltip, niceScale } from './chartKit'
import { C, SERIES, cardSx, tabular } from './tokens'
import { count, dayShort, dayWithWeekday, money } from './format'
import { cardSubSx, cardTitleSx, tabsSx } from './SalesOverTime'
import Empty from './Empty'

const TABS = [
  { key: 'channels', label: 'Channels', head: 'Channel' },
  { key: 'fulfillment', label: 'Fulfillment', head: 'Method' },
  { key: 'payment', label: 'Payment method', head: 'Payment' }
]

/* Rounded top corners on the TOP segment of each stack only — rounding every
   segment leaves notches between them. */
const topOfStack = (ctx) => {
  const { chart, dataIndex, datasetIndex } = ctx
  for (let d = chart.data.datasets.length - 1; d >= 0; d--) {
    if (!chart.isDatasetVisible(d)) continue
    if (Number(chart.data.datasets[d].data[dataIndex]) > 0) return d === datasetIndex
  }
  return false
}

export default function SalesBreakdown({ data, loading }) {
  const [tab, setTab] = useState('channels')
  const t = TABS.find(x => x.key === tab)
  const slices = data?.breakdown?.[tab] || []
  const days = data?.days || []
  const first = loading && !data

  const perDay = days.map((_, j) => slices.reduce((s, sl) => s + (Number(sl.daily[j]) || 0), 0))
  const nothing = !first && (slices.length === 0 || allZero(perDay))

  const chartData = {
    labels: days.map(d => dayShort(d.date)),
    datasets: slices.map((s, i) => ({
      label: s.label,
      data: s.daily.map((v, j) => (days[j]?.closed ? null : v)),
      backgroundColor: SERIES[i % SERIES.length],
      borderRadius: ctx => (topOfStack(ctx) ? { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 } : 0),
      borderSkipped: false,
      // 0.75 × 0.8 = bars at 60% of each day's band.
      barPercentage: 0.75,
      categoryPercentage: 0.8,
      maxBarThickness: 40,
      stack: 'sales'
    }))
  }
  const options = {
    ...baseOptions,
    scales: axes({ money: true, stacked: true, maxXTicks: 8, scale: niceScale([perDay]) }),
    plugins: {
      ...baseOptions.plugins,
      tooltip: htmlTooltip(i => {
        const d = days[i]
        if (!d) return null
        const rows = slices
          .map((s, k) => ({ color: SERIES[k % SERIES.length], square: true, label: s.label, value: money(s.daily[i]), raw: Number(s.daily[i]) || 0 }))
          .filter(r => r.raw > 0)
        return {
          title: `${dayWithWeekday(d.date)}${d.closed ? ' · Closed' : ''}`,
          rows: rows.length ? rows : [{ color: C.muted, square: true, label: 'No sales', value: money(0) }],
          note: `Total ${money(perDay[i])}`
        }
      })
    }
  }

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
      <Typography component="h2" sx={cardTitleSx}>Sales breakdown</Typography>
      <Typography sx={cardSubSx}>{'Where this period’s sales came from'}</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ ...tabsSx, mt: 1.5, borderBottom: `1px solid ${C.border}` }} aria-label="Breakdown">
        {TABS.map(x => <Tab key={x.key} value={x.key} label={x.label} />)}
      </Tabs>

      <Box sx={{ height: 260, mt: 2.5, position: 'relative' }} role="img" aria-label={`Sales by ${t.head.toLowerCase()}: ${slices.map(s => `${s.label} ${s.share}%`).join(', ') || 'no sales'}`}>
        {first ? <Skeleton variant="rounded" height="100%" sx={{ borderRadius: '8px' }} /> : nothing ? <Empty compact message="No sales in this range." /> : <Bar data={chartData} options={options} />}
      </Box>

      {/* The split at a glance: one 100% bar above the table. */}
      {slices.length > 0 ? (
        <Box sx={{ display: 'flex', height: 8, borderRadius: '4px', overflow: 'hidden', mt: 2.5, bgcolor: '#F1F2F4' }} role="img"
          aria-label={`Share of gross sales: ${slices.map(s => `${s.label} ${s.share.toFixed(1)}%`).join(', ')}`}>
          {slices.map((s, i) => (
            <Tooltip key={s.key} title={`${s.label} · ${s.share.toFixed(1)}%`} arrow>
              <Box sx={{ width: `${s.share}%`, bgcolor: SERIES[i % SERIES.length], '& + &': { borderLeft: '2px solid #fff' } }} />
            </Tooltip>
          ))}
        </Box>
      ) : null}

      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 1.5, fontSize: 14, ...tabular }}>
        <Box component="thead">
          <Box component="tr" sx={{ borderBottom: `1px solid ${C.border}` }}>
            {[t.head, 'Orders', '% of gross sales', 'Gross sales'].map((h, i) => (
              <Box component="th" key={h} sx={{ textAlign: i === 0 ? 'left' : 'right', fontWeight: 500, color: C.textMuted, fontSize: 12, py: 1, display: i === 1 ? { xs: 'none', sm: 'table-cell' } : 'table-cell' }}>{h}</Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {slices.length === 0 && !first ? (
            <Box component="tr"><Box component="td" colSpan={4} sx={{ py: 2, color: C.textMuted, fontSize: 13 }}>No sales in this range.</Box></Box>
          ) : null}
          {slices.map((s, i) => (
            <Box component="tr" key={s.key} sx={{ borderBottom: `1px solid ${C.border}`, '&:last-child': { borderBottom: 0 } }}>
              <Box component="td" sx={{ py: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SERIES[i % SERIES.length], flexShrink: 0 }} />
                  <Box component="span" sx={{ color: C.text, fontWeight: 500 }}>{s.label}</Box>
                </Box>
              </Box>
              <Box component="td" sx={{ textAlign: 'right', color: C.textMuted, display: { xs: 'none', sm: 'table-cell' } }}>{count(s.orders)}</Box>
              <Box component="td" sx={{ textAlign: 'right', color: C.text }}>{s.share.toFixed(1)}%</Box>
              <Box component="td" sx={{ textAlign: 'right', color: C.text, fontWeight: 500 }}>{money(s.sales)}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
